import { useAuth } from '../contexts/AuthContext'
import { apiGet, apiPatch } from '../lib/api'
import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useNotification } from '../contexts/NotificationContext'

type EnrollmentStatus = 'pending' | 'approved' | 'rejected' | 'completed'

interface EnrollmentDetail {
  id: string
  organizationName: string
  contactName: string
  contactEmail: string
  contactPhone: string
  location: string
  facilityType: string
  medicalSpecialties: string[]
  currentEmr: string | null
  preferredLanguages: string[]
  branches: number
  staffStrength: number
  deploymentOption: string
  requiredIntegrations: string[]
  additionalInfo: string | null
  status: EnrollmentStatus
  reviewNotes: string | null
  reviewedAt: string | null
  reviewedBy: string | null
  userId: string | null
  createdAt: string
  updatedAt: string
}

export function AdminEnrollmentDetail() {
  const { user } = useAuth()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { showSuccess, showError } = useNotification()
  const [enrollment, setEnrollment] = useState<EnrollmentDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [newStatus, setNewStatus] = useState<EnrollmentStatus>('pending')
  const [reviewNotes, setReviewNotes] = useState('')

  useEffect(() => {
    if ((user?.role === 'admin' || user?.role === 'superadmin') && id) {
      fetchEnrollment()
    }
  }, [user, id])

  const fetchEnrollment = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiGet(`/enrollment/${id}`)
      const data = await response.json()

      if (response.ok && data.success) {
        setEnrollment(data.data.enrollment)
      } else {
        setError(data.error?.message || 'Failed to load enrollment details')
      }
    } catch (err) {
      console.error('Failed to fetch enrollment:', err)
      setError('Failed to load enrollment details')
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800'
      case 'rejected':
        return 'bg-red-100 text-red-800'
      case 'completed':
        return 'bg-blue-100 text-blue-800'
      case 'pending':
      default:
        return 'bg-yellow-100 text-yellow-800'
    }
  }

  const formatLocation = (location: string) => {
    return location
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const openStatusModal = (status: EnrollmentStatus) => {
    setNewStatus(status)
    setReviewNotes(enrollment?.reviewNotes || '')
    setShowStatusModal(true)
  }

  const handleUpdateStatus = async () => {
    if (!id) return

    try {
      setIsUpdating(true)
      const response = await apiPatch(`/enrollment/${id}/status`, {
        status: newStatus,
        reviewNotes: reviewNotes.trim() || undefined,
      })

      const data = await response.json()

      if (response.ok && data.success) {
        showSuccess('Enrollment status updated successfully')
        setShowStatusModal(false)
        setReviewNotes('')
        fetchEnrollment()
      } else {
        showError(data.error?.message || 'Failed to update status')
      }
    } catch (err) {
      console.error('Failed to update enrollment status:', err)
      showError('Failed to update enrollment status')
    } finally {
      setIsUpdating(false)
    }
  }

  if (user?.role !== 'admin' && user?.role !== 'superadmin') {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Access denied. Admin privileges required.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/enrollments')}
          className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
        >
          ← Back to Enrollments
        </button>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Enrollment Details</h1>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-600">Loading enrollment details...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      ) : enrollment ? (
        <div className="space-y-6">
          {/* Status Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Status</h2>
              <div className="flex items-center gap-3">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(enrollment.status)}`}
                >
                  {enrollment.status.charAt(0).toUpperCase() + enrollment.status.slice(1)}
                </span>
              </div>
            </div>

            {/* Status Actions */}
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => openStatusModal('approved')}
                disabled={enrollment.status === 'approved'}
                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ✓ Approve
              </button>
              <button
                onClick={() => openStatusModal('rejected')}
                disabled={enrollment.status === 'rejected'}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ✗ Reject
              </button>
              <button
                onClick={() => openStatusModal('completed')}
                disabled={enrollment.status === 'completed'}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ✓ Complete
              </button>
              <button
                onClick={() => openStatusModal('pending')}
                disabled={enrollment.status === 'pending'}
                className="px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ↺ Set Pending
              </button>
            </div>

            {enrollment.reviewNotes && (
              <div className="mt-4 pt-4 border-t">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Review Notes</h3>
                <p className="text-sm text-gray-900">{enrollment.reviewNotes}</p>
                {enrollment.reviewedAt && (
                  <p className="text-xs text-gray-500 mt-2">
                    Reviewed on {new Date(enrollment.reviewedAt).toLocaleDateString()}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Organization Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Organization Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Organization Name</h3>
                <p className="text-base text-gray-900">{enrollment.organizationName}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Facility Type</h3>
                <p className="text-base text-gray-900">{enrollment.facilityType}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Location</h3>
                <p className="text-base text-gray-900">{formatLocation(enrollment.location)}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Deployment Option</h3>
                <p className="text-base text-gray-900">
                  {enrollment.deploymentOption
                    .split('-')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Number of Branches</h3>
                <p className="text-base text-gray-900">{enrollment.branches}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Staff Strength</h3>
                <p className="text-base text-gray-900">{enrollment.staffStrength}</p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Contact Name</h3>
                <p className="text-base text-gray-900">{enrollment.contactName}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Email</h3>
                <p className="text-base text-gray-900">{enrollment.contactEmail}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Phone</h3>
                <p className="text-base text-gray-900">{enrollment.contactPhone}</p>
              </div>
            </div>
          </div>

          {/* Additional Details */}
          {(enrollment.medicalSpecialties.length > 0 ||
            enrollment.preferredLanguages.length > 0 ||
            enrollment.currentEmr ||
            enrollment.requiredIntegrations.length > 0 ||
            enrollment.additionalInfo) && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Additional Details</h2>
              <div className="space-y-4">
                {enrollment.medicalSpecialties.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Medical Specialties</h3>
                    <div className="flex flex-wrap gap-2">
                      {enrollment.medicalSpecialties.map((specialty, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {enrollment.preferredLanguages.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Preferred Languages</h3>
                    <div className="flex flex-wrap gap-2">
                      {enrollment.preferredLanguages.map((language, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                        >
                          {language}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {enrollment.currentEmr && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Current EMR System</h3>
                    <p className="text-base text-gray-900">{enrollment.currentEmr}</p>
                  </div>
                )}
                {enrollment.requiredIntegrations.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Required Integrations</h3>
                    <div className="flex flex-wrap gap-2">
                      {enrollment.requiredIntegrations.map((integration, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm"
                        >
                          {integration}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {enrollment.additionalInfo && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Additional Information</h3>
                    <p className="text-base text-gray-900 whitespace-pre-wrap">{enrollment.additionalInfo}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Timestamps</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Submitted</h3>
                <p className="text-base text-gray-900">
                  {new Date(enrollment.createdAt).toLocaleString()}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Last Updated</h3>
                <p className="text-base text-gray-900">
                  {new Date(enrollment.updatedAt).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowStatusModal(false)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Update Enrollment Status
              </h3>
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Change status to: <span className="font-medium capitalize">{newStatus}</span>
                </p>
              </div>
              <div className="mb-4">
                <label htmlFor="reviewNotes" className="block text-sm font-medium text-gray-700 mb-1">
                  Review Notes (optional)
                </label>
                <textarea
                  id="reviewNotes"
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add any notes about this status change..."
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateStatus}
                  disabled={isUpdating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {isUpdating ? 'Updating...' : 'Update Status'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
